import { apiClient } from "@/lib/api/client";

/**
 * FCM registration + foreground/tap handlers.
 *
 * MUST be called AFTER login+switch-org because the /api/devices/register endpoint requires
 * a tenant-scoped JWT (RequireOrg policy).
 *
 * The tap handler uses Next.js router for in-app navigation so the static export bundle
 * doesn't hard-reload on deep links. Pass the router from the caller to avoid importing
 * next/navigation here (keeps this module isomorphic-safe for the non-native no-op path).
 */

type CapacitorGlobal = { isNativePlatform?: () => boolean };

const isNative = (): boolean => {
  if (typeof window === "undefined") return false;
  const cap = (window as unknown as { Capacitor?: CapacitorGlobal }).Capacitor;
  return cap?.isNativePlatform?.() === true;
};

export interface RegisterDeviceRequest {
  token: string;
  platform: "android" | "ios";
  appVersion?: string;
}

export interface PushRegistrationOptions {
  onForeground?: (notification: {
    title?: string;
    body?: string;
    data: Record<string, unknown>;
  }) => void;
  onTap: (route: string, data: Record<string, unknown>) => void;
  // Fired once the FCM token is received. AuthContext persists it so logout can explicitly
  // DELETE the device row instead of waiting for FCM garbage-collection.
  onTokenReceived?: (token: string) => void | Promise<void>;
}

let registered = false;

export async function registerPush(opts: PushRegistrationOptions): Promise<void> {
  if (!isNative() || registered) return;

  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");

    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== "granted") {
      console.warn("[push] permission denied");
      return;
    }

    await PushNotifications.register();
    registered = true;

    await PushNotifications.addListener("registration", async ({ value }) => {
      await opts.onTokenReceived?.(value);
      const payload: RegisterDeviceRequest = {
        token: value,
        platform: "android",
        appVersion: import.meta.env.VITE_APP_VERSION,
      };
      try {
        await apiClient.post<void, RegisterDeviceRequest>("/api/devices/register", payload);
      } catch (err) {
        console.error("[push] registration POST failed", err);
      }
    });

    await PushNotifications.addListener("registrationError", (err) => {
      console.error("[push] FCM registration error", err);
    });

    await PushNotifications.addListener("pushNotificationReceived", (notification) => {
      opts.onForeground?.({
        title: notification.title,
        body: notification.body,
        data: (notification.data ?? {}) as Record<string, unknown>,
      });
    });

    await PushNotifications.addListener(
      "pushNotificationActionPerformed",
      ({ notification }) => {
        const data = (notification.data ?? {}) as Record<string, unknown>;
        const route = typeof data.route === "string" ? data.route : "/dashboard";
        opts.onTap(route, data);
      },
    );
  } catch (err) {
    console.error("[push] register failed", err);
  }
}

export async function unregisterPush(): Promise<void> {
  if (!isNative() || !registered) return;
  try {
    const { PushNotifications } = await import("@capacitor/push-notifications");
    await PushNotifications.removeAllListeners();
    registered = false;
  } catch (err) {
    console.error("[push] unregister failed", err);
  }
}
